import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Space, Tooltip, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  clearQuarantineRemnantsManage,
  forceSendRemnantsManage,
  recomputeRemnantsManage,
  setBlockRemnantsManage,
  setInSaleRemnantsManage,
} from '../../../api/RemnantsManage';
import {
  SHEETS_SYNC_IN_SALE_TOOLTIP,
  getSheetsSyncLocks,
  isInSaleLockedBySheetsSync,
  type SheetsSyncLocks,
} from '../../../api/IntegrationSheets';

type Props = {
  article: string;
  onDone: () => void | Promise<void>;
};

export const GoodsFbsActions = ({ article, onDone }: Props) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [sheetsSyncLocks, setSheetsSyncLocks] = useState<SheetsSyncLocks>({
    wildberries: false,
    ozon: false,
  });

  useEffect(() => {
    void getSheetsSyncLocks().then(setSheetsSyncLocks);
  }, []);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
      await onDone();
    } finally {
      setBusy(null);
    }
  };

  const marketplaceMenu = (
    action: (
      mp: 'ozon' | 'wildberries' | 'both',
    ) => Promise<void>,
    opts?: { lockInSale?: boolean },
  ): MenuProps['items'] => {
    const lockInSale = !!opts?.lockInSale;
    const item = (
      mp: 'ozon' | 'wildberries' | 'both',
      label: string,
      key: string,
    ) => ({
      key,
      label,
      disabled: lockInSale && isInSaleLockedBySheetsSync(sheetsSyncLocks, mp),
      onClick: () => void action(mp),
    });
    return [
      item('both', 'WB + Ozon', 'both'),
      item('wildberries', 'Только WB', 'wb'),
      item('ozon', 'Только Ozon', 'ozon'),
    ];
  };

  const saleLocked =
    sheetsSyncLocks.wildberries && sheetsSyncLocks.ozon;

  const saleButton = (
    <Dropdown
      disabled={saleLocked}
      menu={{
        items: [
          {
            key: 'enable',
            label: 'Вернуть в продажу',
            children: marketplaceMenu(async (marketplace) => {
              await run('in_sale', async () => {
                const res = await setInSaleRemnantsManage({
                  articles: [article],
                  marketplace,
                  in_sale: true,
                });
                message.success(
                  `В продаже: WB=${res.updated.wb}, Ozon=${res.updated.ozon}`,
                );
              });
            }, { lockInSale: true }),
          },
          {
            key: 'disable',
            label: 'Снять с продажи',
            children: marketplaceMenu(async (marketplace) => {
              await run('disable_sale', async () => {
                const res = await setInSaleRemnantsManage({
                  articles: [article],
                  marketplace,
                  in_sale: false,
                });
                message.success(
                  `Снято: WB=${res.updated.wb}, Ozon=${res.updated.ozon}`,
                );
              });
            }, { lockInSale: true }),
          },
        ],
      }}
    >
      <Button loading={busy === 'in_sale' || busy === 'disable_sale'}>
        Продажа
      </Button>
    </Dropdown>
  );

  return (
    <Space wrap>
      <Button
        loading={busy === 'recompute'}
        onClick={() =>
          run('recompute', async () => {
            await recomputeRemnantsManage({ articles: [article] });
            message.success('Остатки пересчитаны');
          })
        }
      >
        Пересчитать
      </Button>
      <Dropdown
        menu={{
          items: marketplaceMenu(async (marketplace) => {
            await run('force_send', async () => {
              const res = await forceSendRemnantsManage({
                articles: [article],
                marketplace,
              });
              message.success(
                `Отправлено: WB=${res.sent.wb}, Ozon=${res.sent.ozon}`,
              );
            });
          }),
        }}
      >
        <Button loading={busy === 'force_send'}>Отправить на МП</Button>
      </Dropdown>
      <Dropdown
        menu={{
          items: marketplaceMenu(async (marketplace) => {
            await run('clear_quarantine', async () => {
              const res = await clearQuarantineRemnantsManage({
                articles: [article],
                marketplace,
              });
              message.success(
                `Карантин сброшен: WB=${res.cleared.wb}, Ozon=${res.cleared.ozon}`,
              );
            });
          }),
        }}
      >
        <Button loading={busy === 'clear_quarantine'}>Сбросить карантин</Button>
      </Dropdown>
      <Dropdown
        menu={{
          items: [
            {
              key: 'block',
              label: 'Заблокировать отправку',
              children: marketplaceMenu(async (marketplace) => {
                await run('block', async () => {
                  const res = await setBlockRemnantsManage({
                    articles: [article],
                    marketplace,
                    blocked: true,
                  });
                  message.success(
                    `Блок: WB=${res.updated.wb}, Ozon=${res.updated.ozon}`,
                  );
                });
              }),
            },
            {
              key: 'unblock',
              label: 'Снять блок',
              children: marketplaceMenu(async (marketplace) => {
                await run('unblock', async () => {
                  const res = await setBlockRemnantsManage({
                    articles: [article],
                    marketplace,
                    blocked: false,
                  });
                  message.success(
                    `Блок снят: WB=${res.updated.wb}, Ozon=${res.updated.ozon}`,
                  );
                });
              }),
            },
          ],
        }}
      >
        <Button loading={busy === 'block' || busy === 'unblock'}>Блок</Button>
      </Dropdown>
      {saleLocked ? (
        <Tooltip title={SHEETS_SYNC_IN_SALE_TOOLTIP}>{saleButton}</Tooltip>
      ) : (
        saleButton
      )}
    </Space>
  );
};
